export const API = '/api/v1';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;
let refreshing: Promise<string | null> | null = null;

export function setAccessToken(token: string | null): void { accessToken = token; }
export function getAccessToken(): string | null { return accessToken; }
export function setUnauthenticatedHandler(fn: () => void): void { onUnauthenticated = fn; }

interface RequestOptions {
  method?: string;
  body?: unknown;
  raw?: BodyInit;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** true 면 401 을 받아도 토큰 갱신을 시도하지 않는다 (갱신 요청 자신) */
  noRetry?: boolean;
}

async function parse(res: Response): Promise<unknown> {
  const type = res.headers.get('content-type') ?? '';
  if (res.status === 204) return null;
  if (type.includes('application/json')) return res.json();
  return res.text();
}

function toError(status: number, payload: unknown): ApiError {
  const body = payload as { error?: { code?: string; message?: string; details?: Record<string, unknown> } };
  const err = body?.error;
  return new ApiError(
    status,
    err?.code ?? 'UNKNOWN',
    err?.message ?? '요청을 처리하지 못했습니다',
    err?.details,
  );
}

/** 액세스 토큰 갱신. 동시에 여러 요청이 401 을 받아도 갱신은 한 번만 일어난다. */
async function refreshToken(): Promise<string | null> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) return null;
      const data = (await res.json()) as { access_token: string };
      accessToken = data.access_token;
      return data.access_token;
    } catch {
      return null;
    } finally {
      // 다음 401 때 다시 시도할 수 있도록 비운다
      setTimeout(() => { refreshing = null; }, 0);
    }
  })();
  return refreshing;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const send = async (token: string | null): Promise<Response> => fetch(`${API}${path}`, {
    method: options.method ?? 'GET',
    credentials: 'include',
    ...(options.signal ? { signal: options.signal } : {}),
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
      ...options.headers,
    },
    body: options.raw ?? (options.body !== undefined ? JSON.stringify(options.body) : null),
  });

  let res = await send(accessToken);

  if (res.status === 401 && !options.noRetry) {
    const fresh = await refreshToken();
    if (fresh) res = await send(fresh);
    else onUnauthenticated?.();
  }

  const payload = await parse(res);
  if (!res.ok) throw toError(res.status, payload);
  return payload as T;
}

export const get = <T>(path: string, signal?: AbortSignal): Promise<T> =>
  request<T>(path, signal ? { signal } : {});
export const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', ...(body !== undefined ? { body } : {}) });
export const patch = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body });
export const del = <T>(path: string): Promise<T> =>
  request<T>(path, { method: 'DELETE' });

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}
